import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { BehaviorSubject, Observable, Subscription, catchError, combineLatest, map, of, startWith } from 'rxjs';
import * as uuid from 'uuid';

import {
    AgentAdhocSourceResponse,
    AgentAdhocSourceStatusEnum
} from '../../../api/agents/models/curation-agent.api';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { TableColumnFormatsEnum } from '../../../Common/Forms/enums/table-column-formats.enum';
import { TableColumnInputEnum } from '../../../Common/Forms/enums/table-column-input-type.enum';
import { TableColumnModel } from '../../../Common/Forms/models/table-column.model';
import { TakeOverPanelComponent } from '../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FilterService } from '../../../Common/Filter/services/abstract-filter.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { AgentRouteSectionEnum, agentSectionRoute } from '../../AutomationAgentsRouter/automation-agents-route.const';
import { defaultSourceTypeIcon, sourceTypeIcons, sourceTypeLabels } from '../../enums/source-icons.const';
import { AdHocRunRow } from '../../models/ad-hoc-run-row.model';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { AdHocRunFilterService } from '../../services/ad-hoc-run-filter.service';
import { CurationAgentsService, sourceNameKey } from '../../services/curation-agents.service';
import { AdHocReportPanelComponent } from '../AdHocReportPanel/ad-hoc-report-panel.component';

/**
 * Every ad hoc run on the account, across agents - opened from the gear on an agent's Ad Hoc
 * History, which seeds the filter with that agent.
 */
@Component({
    selector: 'agpr-ad-hoc-agent-management-panel',
    templateUrl: './ad-hoc-agent-management-panel.component.html',
    styleUrls: ['./ad-hoc-agent-management-panel.component.scss'],
    // The panel owns its filters, and the shared filter dropdown and widget read whichever
    // FilterService is in scope - so they read this one
    providers: [
        AdHocRunFilterService,
        {
            provide: FilterService,
            useExisting: AdHocRunFilterService
        }
    ],
    standalone: false
})
export class AdHocAgentManagementPanelComponent extends AbstractDialogComponent
    implements OnInit, AfterViewInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    /** The agent the panel was opened from, which the filter starts on */
    @Input() agentId: number;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;
    @ViewChild('agentCell') agentCell: TemplateRef<any>;
    @ViewChild('sourceCell') sourceCell: TemplateRef<any>;
    @ViewChild('actionsCell') actionsCell: TemplateRef<any>;

    hideSlideOverlay: boolean = false;
    loading: boolean = true;

    columns: TableColumnModel[] = [];
    rows: AdHocRunRow[] = [];

    /**
     * Sorted on by default: the most recent run first. Held apart from the other columns because
     * the table's [sortColumn] needs this same instance, and it must exist before the view is up
     */
    readonly startedColumn: TableColumnModel = {
        heading: 'AutomationAgents.adHoc.management.started',
        field: 'started',
        format: TableColumnFormatsEnum.date,
        sortable: true,
        sortField: 'started',
        sortType: 'numeric',
        width: 180
    };

    /** Account users, so a run can say who submitted it */
    private userNames: Map<number, string> = new Map<number, string>();
    private readonly subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private curationAgentsService: CurationAgentsService,
        private adHocRunFilterService: AdHocRunFilterService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        @Inject(APP_BASE_HREF) private appBaseHref: string
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest([
                this.curationAgentsService.accountAdHocRuns(),
                this.curationAgentsService.agents$,
                this.curationAgentsService.sourceNames$,
                // Who submitted each run, for the Created By column and filter. Empty rather than blocking the
                // table on an account that will not hand its users over
                this.currentUserStateService.getAccountUsers().pipe(startWith([]), catchError(() => of([])))
            ]).pipe(
                map(([runs, agents, sourceNames, users]) => {
                    this.userNames = new Map<number, string>((users ?? []).map(user =>
                        [user.id, `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email]));

                    return { rows: this.toRows(runs, agents, sourceNames), agents: agents };
                })
            ).subscribe(({ rows, agents }) => {
                this.adHocRunFilterService.setRuns(rows, agents);
                this.adHocRunFilterService.selectAgent(this.agentId);
                this.loading = false;
            }),

            // The table draws whatever is left once the filter widget has had its say
            this.adHocRunFilterService.filteredRuns$.subscribe(rows => this.rows = rows)
        );
    }

    /** Cell templates only exist once the view is up, so the columns are completed here */
    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.columns = [
                {
                    heading: 'AutomationAgents.adHoc.agent',
                    type: TableColumnInputEnum.custom,
                    field: 'agentName',
                    customTemplate: this.agentCell,
                    sortable: true,
                    sortField: 'agentName',
                    sortType: 'text'
                },
                {
                    heading: 'AutomationAgents.card.column.source',
                    type: TableColumnInputEnum.custom,
                    field: 'sourceName',
                    customTemplate: this.sourceCell,
                    sortable: true,
                    sortField: 'sourceName',
                    sortType: 'text'
                },
                {
                    heading: 'AutomationAgents.adHoc.management.createdBy',
                    field: 'createdByName',
                    sortable: true,
                    sortField: 'createdByName',
                    sortType: 'text',
                    width: 160
                },
                this.startedColumn,
                {
                    heading: 'AutomationAgents.adHoc.management.completed',
                    field: 'completed',
                    format: TableColumnFormatsEnum.date,
                    sortable: true,
                    sortField: 'completed',
                    sortType: 'numeric',
                    width: 180
                },
                {
                    heading: 'AutomationAgents.adHoc.management.processed',
                    field: 'processed',
                    sortable: true,
                    sortField: 'processed',
                    sortType: 'numeric',
                    width: 120
                },
                {
                    heading: 'AutomationAgents.adHoc.management.curated',
                    field: 'curated',
                    sortable: true,
                    sortField: 'curated',
                    sortType: 'numeric',
                    width: 120
                },
                {
                    heading: 'AutomationAgents.card.column.status',
                    field: 'status',
                    sortable: true,
                    sortField: 'status',
                    sortType: 'text',
                    width: 140
                },
                {
                    heading: '',
                    type: TableColumnInputEnum.custom,
                    field: 'run',
                    customTemplate: this.actionsCell,
                    width: 56
                }
            ];
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /** Opens the coverage the run was submitted over, as the dashboard's row menu does */
    viewSource(row: AdHocRunRow): void {
        this.curationAgentsService.viewAdHocSource(row?.run);
    }

    viewReport(row: AdHocRunRow): void {
        if (!row?.run) {
            return;
        }

        this.coreDialogService.open(AdHocReportPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: this.overlayContainer,
                agent: row.agent,
                run: row.run
            }
        });
    }

    close(): void {
        if (this.takeOverPanel?.isPanelFullyOpened) {
            this.takeOverPanel.close();

            return;
        }

        this.closeDialog();
    }

    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    /** Who submitted the run, for the Created By column and filter */
    private userName(userId: number): string {
        return this.userNames.get(userId) ?? String(userId ?? '');
    }

    private toRows(
        runs: AgentAdhocSourceResponse[],
        agents: CurationAgentModel[],
        sourceNames: Map<string, string>
    ): AdHocRunRow[] {
        const byId = new Map<number, CurationAgentModel>((agents ?? []).map(agent => [agent.id, agent]));

        // A run whose agent is gone (deleted) has nothing to link to or report on, so it is left out
        return (runs ?? []).filter(run => byId.has(run.agentId)).map(run => {
            const agent = byId.get(run.agentId);
            const agentName = agent.name;
            const sourceName = this.sourceName(run, sourceNames);
            const status = this.localizationService.transform(`AutomationAgents.adHoc.status.${run.status}`);

            return {
                run: run,
                agent: agent,
                agentName: agentName,
                agentUrl: this.agentUrl(agent),
                sourceName: sourceName,
                sourceIcon: sourceTypeIcons[run.sourceType as MonitoringSeriesTypeEnum] ?? defaultSourceTypeIcon,
                sourceTypeLabel: sourceTypeLabels[run.sourceType as MonitoringSeriesTypeEnum] ?? '',
                canViewSource: this.curationAgentsService.hasAdHocSource(run),
                started: run.createdAt,
                completed: run.completedAt ?? null,
                processed: run.evaluatedMentions ?? 0,
                curated: run.curatedMentions ?? 0,
                status: status,
                statusValue: run.status,
                isComplete: run.status === AgentAdhocSourceStatusEnum.complete,
                createdByUserId: run.createdByUserId,
                createdByName: this.userName(run.createdByUserId)
            };
        });
    }

    /** What the run covered: the source's own name, or the kind for a run with no source behind it */
    private sourceName(run: AgentAdhocSourceResponse, sourceNames: Map<string, string>): string {
        const named = run?.sourceType && run?.sourceRef
            ? sourceNames.get(sourceNameKey(run.sourceType, run.sourceRef))
            : null;

        return named ?? this.localizationService.transform(`AutomationAgents.adHoc.kind.${run.kind}`);
    }

    private agentUrl(agent: CurationAgentModel): string {
        return this.appBaseHref + agentSectionRoute(agent.id, AgentRouteSectionEnum.dashboard).slice(1);
    }
}
